import { createSlice } from '@reduxjs/toolkit';
import {
  fetchDataAndUpdateStore,
  fetchRemainingData,
  updatePartialData,
  fetchFinalDataWithRMName,
  fetchNotesAndAppendToModule,
  fetchAttachmentsAndAppendToModule,
  fetchCampaignsAndAppendToModule,
  fetchEmailAndAppendToModule,
  fetchTagsAndAppendToModule,
  fetchLeadsAndAppendToModule,
  fetchContactsAndAppendToModule,
  fetchSelectedListAndAppendToModule,
  fetchDetailAndAppendToModule,
  singleRowAddUpdateInStore,
  fetchQuoteItemsAndAppendToModule,
  fetchSalesOrderedItemAndAppendToModule,
  fetchInvoiceSalesOrderedItemAndAppendToModule,
  fetchProductsAndAppendToModule,
  fetchAccountsAndAppendToPriceSheet,
  fetchAccountsAndAppendToProduct,
} from './actions';

const initialState = {
  loading: {},
  data: {},
  error: {},
  paginationLoading: {},
};

// Utility function
function ensureDetailPath(state, tree) {
  if (!state.data[tree[1]]) state.data[tree[1]] = {};
  if (!state.data[tree[1]].details) state.data[tree[1]].details = {};
  if (!state.data[tree[1]].details[tree[0]]) state.data[tree[1]].details[tree[0]] = {};
  return state.data[tree[1]].details[tree[0]];
}

const slice = createSlice({
  name: 'lists',
  initialState,
  reducers: {
    updateGridColumnState: (state, action) => {
      const { moduleKey, columnState } = action.payload;
      state.data[moduleKey] = {
        ...state.data[moduleKey],
        columnState,
      };
    },
    updateUserDetail: (state, action) => {
      if (!state.data['userDetail']) {
        state.data['userDetail'] = {};
      }
      state.data['userDetail'] = {
        ...state.data['userDetail'],
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDataAndUpdateStore.pending, (state, action) => {
        state.loading[action.meta.arg.key] = true;
        state.error[action.meta.arg.key] = null;
        // 'owners' and 'userList' both read from the same GetUsersAsync endpoint — only 'userList'
        // is actually dispatched (see PageLayoutCRM's pageListMapping); mirror its lifecycle onto
        // 'owners' so getOwners/getActiveOwners keep working without a second network call.
        if (action.meta.arg.key === 'userList') {
          state.loading.owners = true;
          state.error.owners = null;
        }
      })
      .addCase(fetchDataAndUpdateStore.fulfilled, (state, action) => {
        state.loading[action.meta.arg.key] = false;
        if (action.payload.status === 200) {
          if (action.meta.arg.key === 'tagsByModule') {
            if (!state.data[action.meta.arg.storeKey]) {
              state.data[action.meta.arg.storeKey] = {};
            }
            state.data[action.meta.arg.storeKey]['tags'] = action.payload?.data?.tags;
          } else if (action.meta.arg.key === 'genericMasterFilteredList') {
            const typeMap = Object.fromEntries(
              action.payload.data.map((item) => [item.type, item.data])
            );
            state.data[action.meta.arg.key] = typeMap;
          } else if (action.meta.arg.key === 'productReferenceLookup') {
            const typeMap = Object.fromEntries(
              action.payload.data.map((item) => [item.type, item.data])
            );
            state.data[action.meta.arg.key] = typeMap;
          } else {
            state.data[action.meta.arg.key] = {
              ...state.data[action.meta.arg.key],
              ...action.payload.data,
            };
            if (action.meta.arg.key === 'userList') {
              state.data.owners = {
                ...state.data.owners,
                ...action.payload.data,
              };
              state.loading.owners = false;
            }
          }
        } else {
          state.error[action.meta.arg.key] =
            `An unexpected error occurred: ${action.payload.status}`;
          if (action.meta.arg.key === 'userList') {
            state.error.owners = `An unexpected error occurred: ${action.payload.status}`;
            state.loading.owners = false;
          }
        }
      })
      .addCase(fetchDataAndUpdateStore.rejected, (state, action) => {
        state.loading[action.meta.arg.key] = false;
        state.error[action.meta.arg.key] = action.error.message;
        if (action.meta.arg.key === 'userList') {
          state.loading.owners = false;
          state.error.owners = action.error.message;
        }
      })
      .addCase(fetchDetailAndAppendToModule.fulfilled, (state, action) => {
        const [response, key, subkey] = action.payload;
        if (response?.status === 200) {
          const detailData = response?.data;
          if (!state.data[key]) {
            state.data[key] = {};
          }
          if (!state.data[key].details) {
            state.data[key].details = {};
          }
          if (!state.data[key].details[detailData?.id]) {
            state.data[key].details[detailData?.id] = {};
          }
          state.data[key].details[detailData?.id] = {
            ...state.data[key].details[detailData?.id],
            ...detailData,
          };
        } else {
          console.log(`An unexpected error occurred: ${response?.status}`);
        }
      })
      .addCase(singleRowAddUpdateInStore.pending, (state, action) => {
        state.loading[action.meta.arg.moduleKey] = true;
      })
      // .addCase(singleRowAddUpdateInStore.fulfilled, (state, action) => {
      //   state.loading[action.meta.arg.moduleKey] = false;
        
      //   const [response, key, subkey] = action.payload;
      //   if (response.status === 200) {
      //     if (action.meta.arg.rowIndex !== undefined) {
      //       // Update existing row
      //       if (!state.data[key]) {
      //         state.data[key] = {};
      //       }
      //       if (!state.data[key][subkey]) {
      //         state.data[key][subkey] = [];
      //       }

      //       state.data[key][subkey][action.meta.arg.rowIndex] = response.data;
      //     } else {
      //       // Add new row
      //       state.data[key][subkey].unshift(response.data);
      //     }
      //     // state.data[key].details[response.data.id].details = response.data;
      //   } else {
      //     state.error[key] = `An unexpected error occurred: ${response.status}`;
      //   }
      // })
      .addCase(singleRowAddUpdateInStore.fulfilled, (state, action) => {
        state.loading[action.meta.arg.moduleKey] = false;

        const [response, key, subkey] = action.payload;
        if (response.status === 200) {
          if (action.meta.arg.rowIndex !== undefined) {
            // Update existing row
            if (!state.data[key]) {
              state.data[key] = {}; // ✅ added
            }
            if (!state.data[key][subkey]) {
              state.data[key][subkey] = []; // ✅ added
            }

            state.data[key][subkey][action.meta.arg.rowIndex] = response.data;
          } else {
            // Add new row
            if (!state.data[key]) {
              state.data[key] = {}; // ✅ added
            }
            if (!state.data[key][subkey]) {
              state.data[key][subkey] = []; // ✅ added
            }

            state.data[key][subkey].unshift(response.data);

            // ✅ added: increment totalCount if it exists
            if (state.data[key].totalCount !== undefined) {
              state.data[key].totalCount += 1;
            }
          }
          // state.data[key].details[response.data.id].details = response.data;
        } else {
          state.error[key] = `An unexpected error occurred: ${response.status}`;
        }
      })

      .addCase(fetchNotesAndAppendToModule.fulfilled, (state, action) => {
        const { notesList, tree } = action.payload;
        if (notesList?.status === 200) {
          const notesData = notesList?.data;
          // Find the specific lead by its ID
          // const module = state.data?.[tree[1]]?.[tree[2]];
          // if (!module) {
          //   state.data['currentModule'].notesList = notesData?.notes;
          // } else {
          // if (!state.data[tree[1]].details) {
          //   state.data[tree[1]].details = {};
          // }
          // if (!state.data[tree[1]].details[tree[0]]) {
          //   state.data[tree[1]].details[tree[0]] = {};
          // }
          // state.data[tree[1]].details[tree[0]].notesList = notesData?.notes;
          const detail = ensureDetailPath(state, tree);
          detail.notesList = notesData?.notes;
          // Find the specific lead by its ID
          // const leadIndex = module.findIndex((lead) => lead.id === tree[0]);
          // if (leadIndex !== -1) {
          //   // Update the lead's notesList immutably
          //   state.data[tree[1]][tree[2]][leadIndex] = {
          //     ...state.data[tree[1]][tree[2]][leadIndex],
          //     notesList: notesData.notes,
          //   };
          // }
          // }
        } else {
          console.log(`An unexpected error occurred: ${notesList?.status}`);
        }
      })
      .addCase(fetchAttachmentsAndAppendToModule.fulfilled, (state, action) => {
        const { attachmentsList, tree } = action.payload;
        if (attachmentsList?.status === 200) {
          const attachmentsData = attachmentsList?.data;
          // const module = state.data?.[tree[1]]?.[tree[2]];
          // if (!module) {
          //   state.data['currentModule'].attachments = attachmentsData?.attachments;
          // } else {
          // if (!state.data[tree[1]].details) {
          //   state.data[tree[1]].details = {};
          // }
          // if (!state.data[tree[1]].details[tree[0]]) {
          //   state.data[tree[1]].details[tree[0]] = {};
          // }
          // state.data[tree[1]].details[tree[0]].attachments = attachmentsData?.attachments;
          const detail = ensureDetailPath(state, tree);
          detail.attachments = attachmentsData?.attachments;
          // const leadIndex = module.findIndex((lead) => lead.id === tree[0]);
          // if (leadIndex !== -1) {
          //   // Update the lead's notesList immutably
          //   state.data[tree[1]][tree[2]][leadIndex] = {
          //     ...state.data[tree[1]][tree[2]][leadIndex],
          //     attachments: attachmentsData.attachments,
          //   };
          // }
          // }
        } else {
          console.log(`An unexpected error occurred: ${attachmentsList?.status}`);
        }
      })
      .addCase(fetchQuoteItemsAndAppendToModule.fulfilled, (state, action) => {
        const { quoteDetails, tree } = action.payload;
        if (quoteDetails?.status === 200) {
          const quotesData = quoteDetails?.data;
          // const module = state.data?.[tree[1]]?.[tree[2]];
          // if (!module) {
          //   state.data['currentModule'].quoteDetails = quotesData?.quoteDetails;
          // } else {
          // if (!state.data[tree[1]].details) {
          //   state.data[tree[1]].details = {};
          // }
          // if (!state.data[tree[1]].details[tree[0]]) {
          //   state.data[tree[1]].details[tree[0]] = {};
          // }
          // state.data[tree[1]].details[tree[0]].quoteDetails = quotesData?.quoteDetails;
          const detail = ensureDetailPath(state, tree);
          detail.quoteDetails = quotesData?.quoteDetails;
          // const leadIndex = module.findIndex((lead) => lead.id === tree[0]);
          // if (leadIndex !== -1) {
          //   // Update the lead's notesList immutably
          //   state.data[tree[1]][tree[2]][leadIndex] = {
          //     ...state.data[tree[1]][tree[2]][leadIndex],
          //     quoteDetails: quotesData.quoteDetails,
          //   };
          // }
          // }
        } else {
          console.log(`An unexpected error occurred: ${quoteDetails?.status}`);
        }
      })
      .addCase(fetchAccountsAndAppendToProduct.fulfilled, (state, action) => {
        const { accountList, tree } = action.payload;
        if (accountList?.status === 200) {
          const quotesData = accountList?.data;
          // const module = state.data?.[tree[1]]?.[tree[2]];
          // if (!module) {
          //   state.data['currentModule'].accountList = quotesData?.productAccountAssociations;
          // } else {
          // if (!state.data[tree[1]].details) {
          //   state.data[tree[1]].details = {};
          // }
          // if (!state.data[tree[1]].details[tree[0]]) {
          //   state.data[tree[1]].details[tree[0]] = {};
          // }
          // state.data[tree[1]].details[tree[0]].accountAssociations =
          //   quotesData?.productAccountAssociations;
          const detail = ensureDetailPath(state, tree);
          detail.accountAssociations = quotesData?.productAccountAssociations;
          // const leadIndex = module.findIndex((lead) => lead.id === tree[0]);
          // if (leadIndex !== -1) {
          //   // Update the lead's notesList immutably
          //   state.data[tree[1]][tree[2]][leadIndex] = {
          //     ...state.data[tree[1]][tree[2]][leadIndex],
          //     accountAssociations: quotesData.productAccountAssociations,
          //   };
          // }
          // }
        } else {
          console.log(`An unexpected error occurred: ${accountList?.status}`);
        }
      })
      .addCase(fetchAccountsAndAppendToPriceSheet.fulfilled, (state, action) => {
        const { accountList, tree } = action.payload;
        if (accountList?.status === 200) {
          const quotesData = accountList?.data;
          // const module = state.data?.[tree[1]]?.[tree[2]];
          // if (!module) {
          //   state.data["currentModule"].accountList =
          //     quotesData?.accountPriceSheetAssociations;
          // } else {
          const detail = ensureDetailPath(state, tree);
          detail.accountAssociations = quotesData?.accountPriceSheetAssociations;
          //   const leadIndex = module.findIndex((lead) => lead.id === tree[0]);
          //   if (leadIndex !== -1) {
          //     // Update the lead's notesList immutably
          //     state.data[tree[1]][tree[2]][leadIndex] = {
          //       ...state.data[tree[1]][tree[2]][leadIndex],
          //       accountAssociations: quotesData.accountPriceSheetAssociations,
          //     };
          //   }
          // }
        } else {
          console.log(`An unexpected error occurred: ${accountList?.status}`);
        }
      })
      .addCase(fetchProductsAndAppendToModule.fulfilled, (state, action) => {
        const { productList, tree } = action.payload;
        if (productList?.status === 200) {
          const productData = productList?.data;
          // const module = state.data?.[tree[1]]?.[tree[2]];
          // if (!module) {
          //   state.data['currentModule'].productList = productData?.productPriceSheetAssociations;
          // } else {
          // if (!state.data[tree[1]].details) {
          //   state.data[tree[1]].details = {};
          // }
          // if (!state.data[tree[1]].details[tree[0]]) {
          //   state.data[tree[1]].details[tree[0]] = {};
          // }
          // state.data[tree[1]].details[tree[0]].productAssociations =
          //   productData?.productPriceSheetAssociations;
          const detail = ensureDetailPath(state, tree);
          detail.productAssociations = productData?.productPriceSheetAssociations;
          // const leadIndex = module.findIndex((lead) => lead.id === tree[0]);
          // if (leadIndex !== -1) {
          //   // Update the lead's notesList immutably
          //   state.data[tree[1]][tree[2]][leadIndex] = {
          //     ...state.data[tree[1]][tree[2]][leadIndex],
          //     productAssociations: productData.productPriceSheetAssociations,
          //   };
          // }
          // }
        } else {
          console.log(`An unexpected error occurred: ${productList?.status}`);
        }
      })
      .addCase(fetchCampaignsAndAppendToModule.fulfilled, (state, action) => {
        const { campaignsList, tree } = action.payload;
        if (campaignsList?.status === 200) {
          const campaignData = campaignsList?.data;
          // Find the specific lead by its ID
          // const module = state.data?.[tree[1]]?.[tree[2]];
          // if (!module) {
          //   state.data['currentModule'].campaign = campaignData?.campaign;
          // } else {
          // if (!state.data[tree[1]].details) {
          //   state.data[tree[1]].details = {};
          // }
          // if (!state.data[tree[1]].details[tree[0]]) {
          //   state.data[tree[1]].details[tree[0]] = {};
          // }
          // state.data[tree[1]].details[tree[0]].campaign = campaignData?.campaign;
          const detail = ensureDetailPath(state, tree);
          detail.campaign = campaignData?.campaign;
          // const leadIndex = module.findIndex((lead) => lead.id === tree[0]);
          // if (leadIndex !== -1) {
          //   state.data[tree[1]][tree[2]][leadIndex] = {
          //     ...state.data[tree[1]][tree[2]][leadIndex],
          //     campaign: campaignData.campaign,
          //   };
          // }
          // }
        } else {
          console.log(`An unexpected error occurred: ${campaignsList?.status}`);
        }
      })
      .addCase(fetchLeadsAndAppendToModule.fulfilled, (state, action) => {
        const { LeadList, tree } = action.payload;
        if (LeadList?.status === 200) {
          const LeadData = LeadList?.data;
          // Find the specific lead by its ID
          // const module = state.data?.[tree[1]]?.[tree[2]];
          // if (!module) {
          //   state.data['currentModule'].lead = LeadData?.leads;
          // } else {
          // if (!state.data[tree[1]].details) {
          //   state.data[tree[1]].details = {};
          // }
          // if (!state.data[tree[1]].details[tree[0]]) {
          //   state.data[tree[1]].details[tree[0]] = {};
          // }
          // state.data[tree[1]].details[tree[0]].lead = LeadData?.leads;
          const detail = ensureDetailPath(state, tree);
          detail.lead = LeadData?.leads;
          // const leadIndex = module.findIndex((lead) => lead.id === tree[0]);
          // if (leadIndex !== -1) {
          //   state.data[tree[1]][tree[2]][leadIndex] = {
          //     ...state.data[tree[1]][tree[2]][leadIndex],
          //     lead: LeadData.leads,
          //   };
          // }
          // }
        } else {
          console.log(`An unexpected error occurred: ${LeadList?.status}`);
        }
      })
      .addCase(fetchContactsAndAppendToModule.fulfilled, (state, action) => {
        const { contactList, tree } = action.payload;
        if (contactList?.status === 200) {
          const contactData = contactList?.data;
          // // Find the specific lead by its ID
          // const module = state.data?.[tree[1]]?.[tree[2]];
          // if (!module) {
          //   state.data['currentModule'].contact = contactList?.contacts;
          // } else {
          // if (!state.data[tree[1]].details) {
          //   state.data[tree[1]].details = {};
          // }
          // if (!state.data[tree[1]].details[tree[0]]) {
          //   state.data[tree[1]].details[tree[0]] = {};
          // }
          // state.data[tree[1]].details[tree[0]].contact = contactData?.contacts;
          const detail = ensureDetailPath(state, tree);
          detail.contact = contactData?.contacts;
          // const contactIndex = module.findIndex(
          //   (contact) => contact.id === tree[0]
          // );
          // if (contactIndex !== -1) {
          //   state.data[tree[1]][tree[2]][contactIndex] = {
          //     ...state.data[tree[1]][tree[2]][contactIndex],
          //     contact: contactData.contacts,
          //   };
          // }
          // }
        } else {
          console.log(`An unexpected error occurred: ${contactList?.status}`);
        }
      })
      .addCase(fetchEmailAndAppendToModule.fulfilled, (state, action) => {
        const { emailList, tree } = action.payload;
        if (emailList?.status === 200) {
          const emailData = emailList?.data;
          // Find the specific lead by its ID
          // const module = state.data?.[tree[1]]?.[tree[2]];
          // if (!module) {
          //   state.data['currentModule'].emails = emailData?.emails;
          // } else {
          // if (!state.data[tree[1]].details) {
          //   state.data[tree[1]].details = {};
          // }
          // if (!state.data[tree[1]].details[tree[0]]) {
          //   state.data[tree[1]].details[tree[0]] = {};
          // }
          // state.data[tree[1]].details[tree[0]].emails = emailData?.emails;
          const detail = ensureDetailPath(state, tree);
          detail.emails = emailData?.emails;
          // const leadIndex = module.findIndex((lead) => lead.id === tree[0]);
          // if (leadIndex !== -1) {
          //   state.data[tree[1]][tree[2]][leadIndex] = {
          //     ...state.data[tree[1]][tree[2]][leadIndex],
          //     emails: emailData.emails,
          //   };
          // }
          // }
        } else {
          console.log(`An unexpected error occurred: ${emailList?.status}`);
        }
      })
      .addCase(fetchTagsAndAppendToModule.fulfilled, (state, action) => {
        const { tagsList, tree } = action.payload;
        if (tagsList?.status === 200) {
          const tagData = tagsList?.data;
          // Find the specific lead by its ID
          // const module = state.data?.[tree[1]]?.[tree[2]];
          // if (!module) {
          //   state.data['currentModule'].tags = tagData?.tags;
          // } else {
          // if (!state.data[tree[1]].details) {
          //   state.data[tree[1]].details = {};
          // }
          // if (!state.data[tree[1]].details[tree[0]]) {
          //   state.data[tree[1]].details[tree[0]] = {};
          // }
          // state.data[tree[1]].details[tree[0]].tags = tagData?.tags;
          const detail = ensureDetailPath(state, tree);
          detail.tags = tagData?.tags;
          // const leadIndex = module.findIndex((lead) => lead.id === tree[0]);
          // if (leadIndex !== -1) {
          //   state.data[tree[1]][tree[2]][leadIndex] = {
          //     ...state.data[tree[1]][tree[2]][leadIndex],
          //     tags: tagData.tags,
          //   };
          // }
          // }
        } else {
          console.log(`An unexpected error occurred: ${tagsList?.status}`);
        }
      })
      .addCase(fetchSalesOrderedItemAndAppendToModule.fulfilled, (state, action) => {
        const { salesOrderedItemList, tree } = action.payload;
        if (salesOrderedItemList?.status === 200) {
          const salesOrderedData = salesOrderedItemList?.data;
          console.log('salesOrderedItemList 239  -->> ', salesOrderedData);
          // Find the specific sale by its ID
          // const module = state.data?.[tree[1]]?.[tree[2]];
          // if (!module) {
          //   console.log(' state.data["currentModule"] 242 ', state.data);
          //   state.data['currentModule'].saleOrderDetails = salesOrderedData?.saleOrderDetails;
          // } else {
          // if (!state.data[tree[1]].details) {
          //   state.data[tree[1]].details = {};
          // }
          // if (!state.data[tree[1]].details[tree[0]]) {
          //   state.data[tree[1]].details[tree[0]] = {};
          // }
          // state.data[tree[1]].details[tree[0]].saleOrderDetails =
          //   salesOrderedData?.saleOrderDetails;
          const detail = ensureDetailPath(state, tree);
          detail.saleOrderDetails = salesOrderedData?.saleOrderDetails;
          // const saleIndex = module.findIndex((sale) => sale.id === tree[0]);
          // if (saleIndex !== -1) {
          //   state.data[tree[1]][tree[2]][saleIndex] = {
          //     ...state.data[tree[1]][tree[2]][saleIndex],
          //     saleOrderDetails: salesOrderedData.saleOrderDetails,
          //   };
          // }
          // }
        } else {
          console.log(`An unexpected error occurred: ${salesOrderedItemList?.status}`);
        }
      })
      .addCase(fetchInvoiceSalesOrderedItemAndAppendToModule.fulfilled, (state, action) => {
        const { invoiceSalesOrderedItemList, tree } = action.payload;
        if (invoiceSalesOrderedItemList?.status === 200) {
          const invoiseSalesOrderedData = invoiceSalesOrderedItemList?.data;
          // Find the specific sale by its ID
          // const module = state.data?.[tree[1]]?.[tree[2]];
          // if (!module) {
          //   state.data['currentModule'].invoiceDetails = invoiseSalesOrderedData?.invoiceDetails;
          // } else {
          // if (!state.data[tree[1]].details) {
          //   state.data[tree[1]].details = {};
          // }
          // if (!state.data[tree[1]].details[tree[0]]) {
          //   state.data[tree[1]].details[tree[0]] = {};
          // }
          // state.data[tree[1]].details[tree[0]].invoiceDetails =
          //   invoiseSalesOrderedData?.invoiceDetails;
          const detail = ensureDetailPath(state, tree);
          detail.invoiceDetails = invoiseSalesOrderedData?.invoiceDetails;
          // const saleIndex = module.findIndex((sale) => sale.id === tree[0]);
          // if (saleIndex !== -1) {
          //   state.data[tree[1]][tree[2]][saleIndex] = {
          //     ...state.data[tree[1]][tree[2]][saleIndex],
          //     invoiceDetails: invoiseSalesOrderedData.invoiceDetails,
          //   };
          // }
          // }
        } else {
          console.log(`An unexpected error occurred: ${invoiceSalesOrderedItemList?.status}`);
        }
      })
      .addCase(fetchSelectedListAndAppendToModule.fulfilled, (state, action) => {
        const { key, selectedList } = action.payload;

        if (!state.data.selectionList) {
          state.data.selectionList = {};
        }

        if (selectedList === null) {
          // ✅ Properly clear the entire selection for the module
          state.data.selectionList[key] = {}; // Not delete — just reset to empty
        } else {
          state.data.selectionList[key] = {
            ...state.data.selectionList[key],
            ...selectedList,
          };
        }
      })

      .addCase(fetchRemainingData.pending, (state, action) => {
        state.paginationLoading[action.meta.arg.key] = true;
        state.error[action.meta.arg.key] = null;
      })
      .addCase(fetchRemainingData.fulfilled, (state, action) => {
        state.paginationLoading[action.meta.arg.key] = false;
        if (action.payload.status === 200) {
          // Check if the key exists in state.data
          if (!state.data[action.meta.arg.key]) {
            state.data[action.meta.arg.key] = action.payload.data;
          } else {
            // Filter out duplicate projects from the new data
            const newList = action.payload.data[action.meta.arg.key].filter((newData) => {
              return !state.data[action.meta.arg.key][action.meta.arg.key].some(
                (existingData) => existingData.id === newData.id
              );
            });

            // Concatenate filtered new projects to existing data
            state.data[action.meta.arg.key][action.meta.arg.key] = [
              ...state.data[action.meta.arg.key][action.meta.arg.key],
              ...newList,
            ];
          }
        } else {
          state.error[action.meta.arg.key] =
            `An unexpected error occurred: ${action.payload.status}`;
        }
      })
      .addCase(fetchRemainingData.rejected, (state, action) => {
        state.paginationLoading[action.meta.arg.key] = false;
        state.error[action.meta.arg.key] = action.error.message;
      })
      .addCase(updatePartialData.pending, (state, action) => {
        state.paginationLoading[action.meta.arg.key] = true;
        state.error[action.meta.arg.key] = null;
      })
      .addCase(updatePartialData.fulfilled, (state, action) => {
        state.paginationLoading[action.meta.arg.key] = false;
        if (action.payload.status === 200) {
          // Check if the key exists in state.data
          if (!state.data[action.meta.arg.key]) {
            state.data[action.meta.arg.key] = action.payload.data;
          } else {
            // Filter out duplicate projects from the new data
            const newList = action.payload.data[action.meta.arg.key].filter((newData) => {
              return !state.data[action.meta.arg.key][action.meta.arg.key].some(
                (existingData) => existingData.id === newData.id
              );
            });

            // Concatenate filtered new projects to existing data
            state.data[action.meta.arg.key][action.meta.arg.key] = [
              ...state.data[action.meta.arg.key][action.meta.arg.key],
              ...newList,
            ];
          }
        } else {
          state.error[action.meta.arg.key] =
            `An unexpected error occurred: ${action.payload.status}`;
        }
      })
      .addCase(updatePartialData.rejected, (state, action) => {
        state.paginationLoading[action.meta.arg.key] = false;
        state.error[action.meta.arg.key] = action.error.message;
      })
      .addCase(fetchFinalDataWithRMName.fulfilled, (state, action) => {
        // state.paginationLoading[action.meta.arg.key] = false;
        if (action?.meta?.arg) {
          // Check if the key exists in state.data
          if (!state?.data['finalDataWithRMName']) {
            state.data['finalDataWithRMName'] = action?.meta.arg.inputData;
          } else {
            state.data['finalDataWithRMName'] = action?.payload;
          }
        } else {
          state.error[action.meta.arg.key] =
            `An unexpected error occurred: ${action?.payload?.status}`;
        }
      });
  },
});

export const { reducer, actions } = slice;
export const { updateGridColumnState, updateUserDetail } = actions;

export default reducer;
